"use client"

import React from "react"
import Link from "next/link"
import { Routes } from "@/lib/routes"
import Image from "next/image"
import { TODO } from "@/types"

interface PlaylistVideo {
   id: string
   videoId: string
   slug: string
   title: string
   thumbnails: any
   publishedAt: Date
   views?: number | bigint
   trending?: {
      all_time_views: number | bigint
   } | null
}

interface PlaylistContainerProps {
   videos: PlaylistVideo[]
   currentVideoId: string
   playlistPathSegment: string
}

const formatViews = (views: number | bigint | undefined) => {
   if (!views) return "0 views"
   const num = typeof views === "bigint" ? Number(views) : views
   if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M views`
   if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K views`
   return `${num} views`
}

const VideoPlaylistContainer = ({ videos, currentVideoId, playlistPathSegment }: PlaylistContainerProps) => {
   return (
      <div className="space-y-3">
         <h3 className="text-lg font-semibold text-title">Playlist</h3>
         <div className="max-h-[450px] space-y-2 overflow-y-auto">
            {videos.map((video) => {
               const isCurrentVideo = video.videoId === currentVideoId
               const views = video.views || 0
               return (
                  <Link
                     key={video.id}
                     href={Routes.client.showEpisode.getPath({
                        videoSlug: video.slug,
                        channelSlug: playlistPathSegment,
                     })}
                  >
                     <div
                        className={`flex cursor-pointer gap-3 rounded-lg p-2 transition-colors ${
                           isCurrentVideo ? "border-l-4 border-orange-500 bg-orange-100" : "hover:bg-gray-100"
                        }`}
                     >
                        <div className="relative aspect-video w-32 flex-shrink-0 overflow-hidden rounded sm:w-36">
                           <Image
                              src={(video.thumbnails as TODO)?.default?.url || "/placeholder.webp"}
                              alt={video.title}
                              fill
                              className="object-cover"
                           />
                        </div>
                        <div className="w-full min-w-0 flex-1 lg:max-w-[35ch]">
                           <p
                              className={`line-clamp-2 text-sm font-medium ${
                                 isCurrentVideo ? "text-orange-900" : "text-title"
                              }`}
                           >
                              {video.title}
                           </p>
                           <p className="mt-1 text-xs text-gray-500">{formatViews(views)}</p>
                        </div>
                        {isCurrentVideo && (
                           <div className="flex items-center justify-center">
                              <span className="rounded-full bg-orange-500 p-1 text-white">
                                 <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                       fillRule="evenodd"
                                       d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                       clipRule="evenodd"
                                    />
                                 </svg>
                              </span>
                           </div>
                        )}
                     </div>
                  </Link>
               )
            })}
         </div>
      </div>
   )
}

export default VideoPlaylistContainer
